import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ReferralCard from "../components/ReferralCard";
import { Package, MapPin, LogOut, FileDown, ShoppingBag, Star } from "lucide-react";
import ChangePasswordCard from "../components/ChangePasswordCard";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import api, { API, eur, formatApiErrorDetail } from "../lib/api";
import { ProductReviewDialog, Stars } from "../components/ProductReviewDialog";

const STATUS = { AWAITING_TRANSFER: ["Attesa bonifico", "bg-amber-100 text-amber-800"], PAID: ["Pagato", "bg-blue-100 text-blue-800"], SHIPPED: ["Spedito", "bg-green-100 text-green-800"], PENDING: ["In attesa", "bg-amber-100 text-amber-800"], PROCESSING: ["In elaborazione", "bg-amber-100 text-amber-800"], FAILED: ["Non pagato", "bg-red-100 text-red-800"], CANCELLED: ["Annullato", "bg-stone-100 text-stone-600"] };

export default function Account() {
  const { user, setUser, logout } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [profile, setProfile] = useState({ name: "", phone: "", address: "", zip: "", city: "", province: "" });
  const [reviews, setReviews] = useState({});
  const [reviewing, setReviewing] = useState(null);
  const loadReviews = () => api.get("/me/reviews").then(({ data }) => setReviews(Object.fromEntries(data.map((r) => [r.product_id, r])))).catch(() => {});

  useEffect(() => {
    document.title = "Il mio account — Guatemalan Spirits";
    api.get("/me/orders").then(({ data }) => setOrders(data)).catch(() => {});
    loadReviews();
  }, []);
  useEffect(() => { if (user) setProfile({ name: user.name || "", phone: user.phone || "", address: user.address || "", zip: user.zip || "", city: user.city || "", province: user.province || "" }); }, [user]);

  const save = async () => {
    try { const { data } = await api.put("/me/profile", profile); setUser(data); toast.success("Dati salvati"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const f = (k) => ({ value: profile[k], onChange: (e) => setProfile({ ...profile, [k]: e.target.value }), className: "mt-1 h-11" });

  return (
    <div className="paper-grain min-h-screen">
      <Navbar />
      <section className="max-w-6xl mx-auto px-6 pt-12 pb-20" data-testid="account-page">
        <div className="flex flex-wrap items-start justify-between gap-4 mb-8">
          <div>
            <div className="eyebrow text-[#C2593F] mb-2">Il mio account</div>
            <h1 className="font-serif text-3xl sm:text-4xl font-semibold text-[#0B2A45]" data-testid="account-name">Ciao, {user?.name}</h1>
            <p className="text-sm text-stone-500 mt-1">{user?.email}{user?.role === "b2b" && user?.agent_name && <> · Il tuo agente: <b>{user.agent_name}</b></>}</p>
          </div>
          <div className="flex gap-2">
            {user?.role === "b2b" && <Button asChild className="rounded-full bg-[#0F4C81] text-white"><a href="/listino" data-testid="account-listino-link">Listino operatori</a></Button>}
            <Button asChild variant="outline" className="rounded-full"><a href="/negozio" data-testid="account-shop-link"><ShoppingBag className="w-4 h-4 mr-2" />Negozio</a></Button>
            <Button variant="outline" onClick={async () => { await logout(); navigate("/"); }} className="rounded-full" data-testid="account-logout-btn"><LogOut className="w-4 h-4 mr-2" />Esci</Button>
          </div>
        </div>

        <div className="grid lg:grid-cols-5 gap-8">
          <div className="lg:col-span-3 space-y-3">
            <ReferralCard />
            <h2 className="font-serif text-xl font-semibold text-[#0B2A45] flex items-center gap-2"><Package className="w-5 h-5 text-[#0F4C81]" />I miei ordini <span className="text-stone-400 text-base">({orders.length})</span></h2>
            {orders.length === 0 && <div className="bg-white rounded-2xl border border-stone-200 p-8 text-center text-stone-400" data-testid="account-no-orders">Nessun ordine ancora. <a href="/negozio" className="text-[#0F4C81] underline">Scopri il negozio</a></div>}
            {orders.map((o) => {
              const [label, cls] = STATUS[o.status] || [o.status, "bg-stone-100 text-stone-700"];
              return (
                <div key={o.order_id} className="bg-white rounded-2xl border border-stone-200 p-4" data-testid={`account-order-${o.order_id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div><div className="text-xs text-stone-500">{new Date(o.created_at).toLocaleDateString("it-IT")} · {o.order_id}</div>
                      <div className="text-sm text-stone-700 mt-1">{(o.items || []).map((it) => `${it.quantity}× ${it.name}`).join(", ")}</div>
                      {o.tracking && <div className="text-xs text-stone-500 mt-1">Tracking: <b>{o.tracking}</b></div>}</div>
                    <div className="text-right shrink-0"><div className="font-mono font-semibold text-[#0F4C81]">{eur(o.total)}</div><span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${cls}`}>{label}</span></div>
                  </div>
                  {(o.status === "PAID" || o.status === "SHIPPED") && <a href={`${API}/shop/orders/${o.order_id}/invoice.pdf`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-[#0F4C81] mt-2"><FileDown className="w-3.5 h-3.5" />Ricevuta PDF</a>}
                  {(o.status === "PAID" || o.status === "SHIPPED") && (
                    <div className="mt-3 pt-3 border-t border-stone-100 flex flex-wrap gap-2" data-testid={`order-reviews-${o.order_id}`}>
                      {(o.items || []).map((it) => {
                        const r = reviews[it.product_id];
                        return (
                          <button key={it.product_id} type="button" onClick={() => setReviewing(it)} data-testid={`review-btn-${it.product_id}`}
                            className="inline-flex items-center gap-2 text-xs rounded-full border border-stone-200 px-3 py-1.5 hover:bg-[#FEF9C3] transition-colors">
                            {r ? <><Stars value={r.rating} size="w-3 h-3" /><span className="text-stone-600">Modifica</span></> : <><Star className="w-3.5 h-3.5 text-[#EAB308]" />Valuta {it.name}</>}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl border border-stone-200 p-6 space-y-3" data-testid="account-profile">
              <h2 className="font-serif text-xl font-semibold text-[#0B2A45] flex items-center gap-2"><MapPin className="w-5 h-5 text-[#0F4C81]" />Dati e indirizzo</h2>
              <div><Label>Nome</Label><Input data-testid="account-profile-name" {...f("name")} /></div>
              <div><Label>Telefono</Label><Input {...f("phone")} /></div>
              <div><Label>Indirizzo</Label><Input data-testid="account-profile-address" {...f("address")} /></div>
              <div className="grid grid-cols-6 gap-3">
                <div className="col-span-2"><Label>CAP</Label><Input {...f("zip")} /></div>
                <div className="col-span-3"><Label>Città</Label><Input {...f("city")} /></div>
                <div className="col-span-1"><Label>Prov.</Label><Input maxLength={2} {...f("province")} /></div>
              </div>
              <Button onClick={save} className="w-full h-11 rounded-full bg-[#0F4C81] text-white" data-testid="account-profile-save">Salva</Button>
            </div>
            <ChangePasswordCard />
          </div>
        </div>
      </section>
      {reviewing && <ProductReviewDialog item={reviewing} existing={reviews[reviewing.product_id]} onClose={() => setReviewing(null)} onSaved={loadReviews} />}
      <Footer />
    </div>
  );
}
